import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormArray, AbstractControl, FormControl } from '@angular/forms';
import { FullLoaderService } from 'src/app/shared/services/full-loader.service';
import { ServicePoTokenAuthService } from 'src/app/shared/services/service-po-token-auth.service';
import { QuoteService } from '../../dashboard/quote/quote.service';
import {   KycService,
  KycDashboardRow,
  KycTimelineEntry,
  KycLookups, } from 'src/app/views/settings/kyc/kyc.service';
  import { KycStatus, KycStatusService } from 'src/app/shared/services/kyc-status.service';
// import * as html2pdf from 'html2pdf.js';
import { SettingsService } from 'src/app/views/settings/settings.service';


interface KycTimelineStep {
  label: string;
  description?: string;
  meta?: string;
  state: 'done' | 'current' | 'pending';
}

interface KycRow {
  kycId: number;
  reference: string;
  supplier: string;
  status: KycDashboardRow['status'];
  updateDate: string;
  timeline: KycTimelineStep[];
  // Admin's most recent note for CLARIFICATION_REQUIRED / REJECTED rows.
  // Resolves to clarificationNote when status is CLARIFICATION_REQUIRED and
  // to adminFeedback when REJECTED; null otherwise so the panel hides.
  adminNote: string | null;
}


export type ProfileSetupPromptMode = 'KYC' | 'PROFILE';

export type KycPromptStatus =
  | 'NOT_STARTED'
  | 'IN_PROGRESS'
  | 'SUBMITTED'
  | 'UNDER_REVIEW'
  | 'CLARIFICATION_REQUIRED'
  | 'REJECTED'
  | 'APPROVED';

export interface ProfileSetupPromptData {
  mode: ProfileSetupPromptMode;
  kycReferenceNumber?: string | null;
  kycStatus?: KycPromptStatus;
}

interface KycCopy {
  variant: 'start' | 'resume' | 'review' | 'clarify' | 'rejected';
  iconName: string;
  title: string;
  body: string;
  /** When null, the dialog only shows the close/dismiss button (informational state). */
  ctaLabel: string | null;
  laterLabel: string;
  /** Show the 3-step "what happens next" list. */
  showSteps: boolean;
}

@Component({
  // Issue 7 — force Material form-field labels on this screen to float to
  // the top so they never rest over / overlap the input value.
  providers: [
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { floatLabel: 'always' } },
  ],
  selector: 'app-service-po-token-details',
  templateUrl: './servicePoTokenDetails.component.html',
  styleUrls: ['./servicePoTokenDetails.component.css']
})
export class ServicePoTokenDetailsComponent implements OnInit {

  // Section expand/collapse flags
  isForm1Expand: boolean = true;
  isForm2Expand: boolean = true;
  isForm3Expand: boolean = true;
  isForm4Expand: boolean = true;
  isForm5Expand: boolean = true;
  isForm6Expand: boolean = true;
  isForm7Expand: boolean = true;
  isForm8Expand: boolean = true;
  isForm9Expand: boolean = true;
  isForm10Expand: boolean = true;

  // Route params
  purchaseOrderId!: number;

  // Form groups
  costSummaryFormGroup!: UntypedFormGroup;
  headerFormGroup!: UntypedFormGroup;
  paymentFormGroup!: UntypedFormGroup;
  lineItemTableForm!: UntypedFormGroup;

  // Data
  dataSource: any[] = [];
  headerResponse: any = null;
  uploadedAdditionalAttachmentsArray: any[] = [];

  // Carousel
  previewSection: boolean = false;
  previewDoc: any[] = [];
  currentIndex = 0;

  // Freight toggle
  showFreightDetails: boolean = false;

  // Current date for exchange rate display
  currentdate = new Date();

  // Supplier
  buyerLogo: string = '';
  supplierLogo: string = '';

  // Accept/Reject workflow
  showAcceptOrder: boolean = false;
  showRejectOrder: boolean = false;
  showOrderConfirmed: boolean = false;
  orderConfirmationNumber: string = '';
  deliveryDate!: Date;
  shippingDate!: Date;
  rejectionReasons: { id: number; reason: string }[] = [];
  rejectionReason!: number;
  rejectionMessage: string = '';

  // Extra header fields for accept/reject
  buyerId!: number;
  rfqId!: number;
  quotationId!: number;
  buyerName: string = '';
  supplierName: string = '';

  reviewTooltip = `Please review the PO thoroughly before clicking Accept. Once the Purchase Order (PO) is accepted, it cannot be modified or changed.`;


  // KYC
  isKycCompleted:boolean=false
  showKycAlert:boolean=true
  kycLoader:boolean=true
  showWizard: boolean = false;
  selectedKycId: number | null = null;
  selectedReference: string = '';
  selectedReadOnly: boolean = false;
  startingKyc: boolean = false;
  kycRows: KycRow[] = [];
  lookups: KycLookups | null = null;
  userkycStatus: KycPromptStatus = 'NOT_STARTED';
  supplierId!: any;
  companyId!: any;

  
  constructor(
    private snack: MatSnackBar,
    private _formBuilder: UntypedFormBuilder,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private fullLoaderService: FullLoaderService,
    private activatedRoute: ActivatedRoute,
    private servicePoTokenAuth: ServicePoTokenAuthService,
    private quoteService: QuoteService,
    private kycService: KycService,
    private kycStatus: KycStatusService,
    private settingsService:SettingsService
  ) {}

  get rowControls(): AbstractControl[] {
    return (this.lineItemTableForm.get('rows') as UntypedFormArray).controls;
  }

  getRowControl(rowIndex: number, field: string): FormControl {
    return this.rowControls[rowIndex].get(field) as FormControl;
  }

  async ngOnInit() {
    // Validate token session
    const token = this.servicePoTokenAuth.getToken();
    if (!token) {
      this.snack.open('Session expired. Please re-enter your Service PO token.', 'OK', {
        duration: 4000,
      });
      this.router.navigate(['/servicePoTokenLogin']);
      return;
    }

    this.supplierId = Number(this.servicePoTokenAuth.getSupplierId())
    this.companyId = Number(this.servicePoTokenAuth.getCompanyIdId())

    this.fullLoaderService.showLoader('Fetching Purchase order details, hold on...');

    // Initialize form groups
    this.costSummaryFormGroup = this._formBuilder.group({
      lineItemListPriceSubtotal: [0],
      lineItemDiscount: [0],
      lineItemSubtotal: [0],
      generalDiscountType: [''],
      generalDiscountValue: [0],
      lineItemApplicableTaxProfile: [''],
      lineItemTaxAmount: [0],
      quoteLineItemFreight: [[]],
      lineItemFreightTotal: [0],
      lineItemTotal: [0],
      currency: [null]
    });

    this.headerFormGroup = this._formBuilder.group({
      // PO Details
      purchaseOrderRefNumber: [''],
      purchaseOrderDate: [''],
      rfqRefNumber: [''],
      rfqDate: [''],
      quoteRefNumber: [''],
      quotationDate: [''],
      expectedDeliveryDate: [''],
      // Buyer Company
      buyerCompanyName: [''],
      buyerEmail: [''],
      buyerPhone: [''],
      buyerFax: [''],
      buyerAddress: [''],
      // Buyer Contact
      buyerContactPerson: [''],
      buyerContactEmail: [''],
      buyerContactPhone: [''],
      buyerJobTitle: [''],
      buyerContactFax: [''],
      buyerContactAddress: [''],
      // Supplier
      supplierContactName: [''],
      supplierEmail: [''],
      supplierPhone: [''],
      supplierFax: [''],
      supplierCompanyEmail: [''],
      supplierCompanyName: [''],
      supplierAddress: [''],
      // Vessel
      vesselName: [''],
      vesselIMONumber: [''],
      vesselType: [''],
      vesselClass: [''],
      // Port
      portName: [''],
      portCode: [''],
      vesselETA: [''],
      vesselETD: [''],
      vesselEtaTime: [''],
      vesselEtdTime: ['']
    });

    this.paymentFormGroup = this._formBuilder.group({
      // Buyer Payment Terms
      billingAddress: [''],
      applicableTaxProfile: [''],
      buyersPaymentTerms: [''],
      buyerTermsAndConditions: [''],
      // Port Agent
      portAgencyName: [''],
      portAgentName: [''],
      portAgentPhoneNumber: [''],
      portAgentContact: [''],
      portAgentPhoneFax: [''],
      portAgentEmail: [''],
      portAgentAddress: [''],
      // T&C
      termsAndConditions: ['']
    });

    this.lineItemTableForm = this._formBuilder.group({
      rows: this._formBuilder.array([])
    });

    const params = this.activatedRoute.snapshot.queryParams;

    if (params['servicePurchaseOrderId'] || params['purchaseOrderId']) {
      this.purchaseOrderId = params['servicePurchaseOrderId'] || params['purchaseOrderId'];
      await this.loadKycStatus(this.supplierId)
      await this.loadPurchaseOrderData();
    }

    this.fullLoaderService.hideLoader();
  }

  async loadPurchaseOrderData() {
    try {
      // Fetch all 3 sections in parallel
      const [lineItemsResult, headerResult, paymentTermsResult] = await Promise.all([
        this.quoteService.fetchServicePurchaseOrderByServicePoToken({
          purchaseOrderId: this.purchaseOrderId,
          section: 'lineItems'
        }),
        this.quoteService.fetchServicePurchaseOrderByServicePoToken({
          purchaseOrderId: this.purchaseOrderId,
          section: 'header'
        }),
        this.quoteService.fetchServicePurchaseOrderByServicePoToken({
          purchaseOrderId: this.purchaseOrderId,
          section: 'paymentTerms'
        })
      ]);

      // Populate line items + cost summary
      if (lineItemsResult && lineItemsResult.length > 0) {
        this.populateLineItems(lineItemsResult[0]);
      }

      // Populate header
      if (headerResult && headerResult.length > 0) {
        this.headerResponse = headerResult[0];
        this.populateHeader(headerResult[0]);
      }

      // Populate payment terms
      if (paymentTermsResult && paymentTermsResult.length > 0) {
        this.populatePaymentTerms(paymentTermsResult[0]);
      }
    } catch (error: any) {
      console.error('Error loading service PO data:', error);
      this.snack.open(error?.message || 'Failed to load Purchase Order', 'OK', { duration: 3000 });
    }
  }

  populateLineItems(lineData: any) {
    // Cost summary
    let currency = lineData.currency;
    if (typeof currency === 'string') {
      try { currency = JSON.parse(currency); } catch (e) { currency = null; }
    }

    let freight = lineData.quoteLineItemFreight;
    if (typeof freight === 'string') {
      try { freight = JSON.parse(freight); } catch (e) { freight = []; }
    }

    this.costSummaryFormGroup.patchValue({
      lineItemListPriceSubtotal: lineData.lineItemListPriceSubtotal || 0,
      lineItemDiscount: lineData.lineItemDiscount || 0,
      lineItemSubtotal: lineData.lineItemSubtotal || 0,
      generalDiscountType: lineData.generalDiscountType || '',
      generalDiscountValue: lineData.generalDiscountValue || 0,
      lineItemApplicableTaxProfile: lineData.lineItemApplicableTaxProfile || '',
      lineItemTaxAmount: lineData.lineItemTaxAmount || 0,
      quoteLineItemFreight: freight || [],
      lineItemFreightTotal: lineData.lineItemFreightTotal || 0,
      lineItemTotal: lineData.lineItemTotal || 0,
      currency: currency
    });

    // Line items table
    let quotationLineItems = lineData.quotationLineItems;
    if (typeof quotationLineItems === 'string') {
      try { quotationLineItems = JSON.parse(quotationLineItems); } catch (e) { quotationLineItems = []; }
    }

    if (quotationLineItems && quotationLineItems.length > 0) {
      this.dataSource = quotationLineItems;
      this.populateForm(quotationLineItems);
    }
  }

  populateForm(data: any[]) {
    const rows = this.lineItemTableForm.get('rows') as UntypedFormArray;
    rows.clear();

    data.forEach((item: any, index: number) => {
      rows.push(this._formBuilder.group({
        position: [index + 1],
        serviceName: [item.serviceName || ''],
        serviceDescription: [item.serviceDescription || ''],
        scopeOfWork: [item.scopeOfWork || ''],
        buyerComment: [item.buyerComment || ''],
        supplierComment: [item.supplierComment || ''],
        uom: [item.uom || ''],
        supplierUom: [item.supplierUom || ''],
        quantity: [item.quantity || 0],
        supplierQuantity: [item.supplierQuantity || 0],
        unitCost: [item.unitCost || 0],
        discount: [item.discount || 0],
        discountedUnitPrice: [item.discountedUnitPrice || 0],
        totalCost: [item.totalCost || 0],
        leadTime: [item.leadTime || ''],
        isActive: [item.isActive]
      }));
    });
  }

  populateHeader(header: any) {
    this.buyerLogo = header.buyerOrganizationLogo || '';
    this.supplierLogo = header.supplierOrganizationLogo || '';
    this.buyerId = header.buyerId;
    this.rfqId = header.serviceRfqId;
    this.quotationId = header.serviceQuotationId;
    this.buyerName = header.buyerName || '';
    this.supplierName = header.supplierName || '';
    this.headerFormGroup.patchValue({
      purchaseOrderRefNumber: header.purchaseOrderRefNumber,
      purchaseOrderDate: header.purchaseOrderDate,
      rfqRefNumber: header.rfqRefNumber,
      rfqDate: header.rfqDate,
      quoteRefNumber: header.quoteRefNumber,
      quotationDate: header.quotationDate,
      expectedDeliveryDate: header.expectedDeliveryDate,
      buyerCompanyName: header.buyerCompanyName,
      buyerEmail: header.buyerEmail,
      buyerPhone: header.buyerPhone,
      buyerFax: header.companyFax,
      buyerAddress: header.companyAddress,
      buyerContactPerson: header.buyerContactPerson,
      buyerContactEmail: header.buyerEmail,
      buyerContactPhone: header.buyerPhone,
      buyerJobTitle: header.buyerJobTitle,
      buyerContactFax: header.buyerFax,
      buyerContactAddress: header.buyerAddress,
      supplierContactName: header.supplierName,
      supplierEmail: header.supplierEmail,
      supplierPhone: header.supplierPhone,
      supplierFax: header.supplierFax,
      supplierCompanyEmail: header.supplierCompanyEmail,
      supplierCompanyName: header.supplierCompanyName,
      supplierAddress: header.supplierAddress,
      vesselName: header.vesselName,
      vesselIMONumber: header.vesselImoNumber,
      vesselType: header.vesselType,
      vesselClass: header.class,
      portName: header.portName,
      portCode: header.portCode,
      vesselETA: header.vesselEta,
      vesselETD: header.vesselEtd,
      vesselEtaTime: header.vesselEtaTime,
      vesselEtdTime: header.vesselEtdTime
    });
  }

  populatePaymentTerms(payment: any) {
    this.paymentFormGroup.patchValue({
      billingAddress: payment.billingAddress,
      applicableTaxProfile: payment.applicableTaxProfile,
      buyersPaymentTerms: payment.buyersPaymentTerms,
      buyerTermsAndConditions: payment.termsAndConditions,
      portAgencyName: payment.portAgencyName,
      portAgentName: payment.portAgentName,
      portAgentPhoneNumber: payment.portAgentPhoneNumber,
      portAgentContact: payment.portAgentContact,
      portAgentPhoneFax: payment.portAgentPhoneFax,
      portAgentEmail: payment.emailId,
      portAgentAddress: payment.address,
      termsAndConditions: payment.termsAndConditions
    });

    let attachments = payment.paymentTermsAttachments;
    if (typeof attachments === 'string') {
      try { attachments = JSON.parse(attachments); } catch (e) { attachments = []; }
    }
    this.uploadedAdditionalAttachmentsArray = attachments || [];
  }

  // Download PO PDF
  async downloadPO() {
    this.fullLoaderService.showLoader('Download in progress. Please hold on...');
    try {
      const payload = this.buildServicePurchaseOrderPayload();
      const pdf = await this.quoteService.generateServicePoPdfByServicePoToken(payload);

      const uint8Array = new Uint8Array(pdf.data);
      const blob = new Blob([uint8Array], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = 'PO-' + (this.headerFormGroup.get('purchaseOrderRefNumber')?.value || 'ServicePO') + '.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Download PO error:', error);
      this.snack.open(error?.message || 'Failed to download PO', 'OK', { duration: 3000 });
    }
    this.fullLoaderService.hideLoader();
  }

  private buildServicePurchaseOrderPayload(): any {
    const header = this.headerFormGroup?.value || {};
    const payment = this.paymentFormGroup?.value || {};
    const pricing = this.costSummaryFormGroup?.value || {};
    const lineItemsFormArray = (this.lineItemTableForm?.get('rows') as UntypedFormArray);
    const lineItems = lineItemsFormArray.controls.map((ctrl: any) => ctrl.value);

    const freightArr = Array.isArray(pricing.quoteLineItemFreight) ? pricing.quoteLineItemFreight : [];

    return {
      ids: {
        purchaseOrderId: this.purchaseOrderId,
        rfqId: this.headerResponse?.serviceRfqId,
        quoteId: this.headerResponse?.serviceQuotationId
      },
      purchaseOrder: {
        purchaseOrderRefNumber: header.purchaseOrderRefNumber,
        purchaseOrderDate: header.purchaseOrderDate,
        rfqRefNumber: header.rfqRefNumber,
        rfqDate: header.rfqDate,
        quoteRefNumber: header.quoteRefNumber,
        quotationDate: header.quotationDate,
        expectedDeliveryDate: header.expectedDeliveryDate
      },
      buyer: {
        company: {
          name: header.buyerCompanyName,
          email: header.buyerEmail,
          phone: header.buyerPhone,
          fax: header.buyerFax,
          address: header.buyerAddress
        },
        contact: {
          person: header.buyerContactPerson,
          email: header.buyerContactEmail,
          phone: header.buyerContactPhone,
          fax: header.buyerContactFax,
          address: header.buyerContactAddress
        }
      },
      supplierDetails: {
        company: {
          name: header.supplierCompanyName,
          email: header.supplierCompanyEmail,
          phone: header.supplierPhone,
          fax: header.supplierFax,
          address: header.supplierAddress
        },
        contact: {
          contactName: header.supplierContactName,
          email: header.supplierEmail,
          phone: header.supplierPhone,
          fax: header.supplierFax
        }
      },
      vessel: {
        name: header.vesselName,
        imoNumber: header.vesselIMONumber,
        type: header.vesselType,
        class: header.vesselClass
      },
      port: {
        name: header.portName,
        code: header.portCode,
        eta: header.vesselETA,
        etd: header.vesselETD
      },
      portAgent: {
        portAgencyName: payment.portAgencyName,
        name: payment.portAgentName,
        phoneNumber: payment.portAgentPhoneNumber,
        contact: payment.portAgentContact,
        phoneFax: payment.portAgentPhoneFax,
        email: payment.portAgentEmail,
        address: payment.portAgentAddress
      },
      priceDetails: {
        currency: pricing.currency,
        listPriceSubtotal: pricing.lineItemListPriceSubtotal,
        discount: pricing.lineItemDiscount,
        subtotal: pricing.lineItemSubtotal,
        generalDiscount: {
          type: pricing.generalDiscountType,
          value: pricing.generalDiscountValue
        },
        freight: freightArr,
        freightTotal: pricing.lineItemFreightTotal,
        applicableTaxProfile: pricing.lineItemApplicableTaxProfile,
        taxAmount: pricing.lineItemTaxAmount,
        total: pricing.lineItemTotal
      },
      termsAndConditions: {
        billingAddress: payment.billingAddress,
        applicableTaxProfile: payment.applicableTaxProfile,
        buyersPaymentTerms: payment.buyersPaymentTerms,
        text: payment.termsAndConditions
      },
      lineItems: lineItems.map((li: any, idx: number) => ({
        position: li.position ?? idx + 1,
        serviceName: li.serviceName ?? '',
        serviceDescription: li.serviceDescription ?? '',
        buyerComment: li.buyerComment ?? '',
        supplierComment: li.supplierComment ?? '',
        uom: li.uom ?? '',
        supplierUom: li.supplierUom ?? '',
        quantity: li.quantity ?? 0,
        supplierQuantity: li.supplierQuantity ?? 0,
        unitCost: li.unitCost ?? 0,
        discount: li.discount ?? 0,
        discountedUnitPrice: li.discountedUnitPrice ?? 0,
        totalCost: li.totalCost ?? 0,
        leadTime: li.leadTime ?? '',
        isActive: li.isActive ?? 1
      })),
      documents: {
        poPdfPath: null
      }
    };
  }

  // Section toggle
  toggleVisibility(section: string) {
    switch (section) {
      case 'form1': this.isForm1Expand = !this.isForm1Expand; break;
      case 'form2': this.isForm2Expand = !this.isForm2Expand; break;
      case 'form3': this.isForm3Expand = !this.isForm3Expand; break;
      case 'form4': this.isForm4Expand = !this.isForm4Expand; break;
      case 'form5': this.isForm5Expand = !this.isForm5Expand; break;
      case 'form6': this.isForm6Expand = !this.isForm6Expand; break;
      case 'form7': this.isForm7Expand = !this.isForm7Expand; break;
      case 'form8': this.isForm8Expand = !this.isForm8Expand; break;
      case 'form9': this.isForm9Expand = !this.isForm9Expand; break;
      case 'form10': this.isForm10Expand = !this.isForm10Expand; break;
    }
  }

  toggleFreightDetails() {
    this.showFreightDetails = !this.showFreightDetails;
  }

  // Close and go back to supplier service PO list
  close() {
    this.servicePoTokenAuth.clear();
    this.router.navigate(['/servicePoTokenLogin']);
  }

  // Attachment preview carousel
  getHeaderAttachmentCount(): number {
    return this.uploadedAdditionalAttachmentsArray?.length || 0;
  }

  togglePreview(show: boolean, type: string) {
    if (show && type === 'generalAttachment') {
      this.previewDoc = this.uploadedAdditionalAttachmentsArray.map((att: any) => ({
        ...att,
        dataUrl: att.dataUrl || att.s3_path || ''
      }));
      this.currentIndex = 0;
    }
    this.previewSection = show;
  }

  isPdf(url: string): boolean {
    return url?.toLowerCase()?.endsWith('.pdf') || false;
  }

  prevSlide() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    }
  }

  nextSlide() {
    if (this.currentIndex < this.previewDoc.length - 1) {
      this.currentIndex++;
    }
  }

  // Create Invoice navigation
  createInvoice() {
    this.snack.open('Please register and log in to the portal to create invoices.', 'OK', {
      duration: 4000,
    });
  }

  // Accept/Reject workflow methods

  async acceptPO() {
    if(this.userkycStatus != 'APPROVED'){
      this.snack.open('Accept action is disabled until KYC is approved', 'OK', { duration: 2500 });
      return;
    }
    this.fullLoaderService.showLoader();
    await this.getLastAcceptedOrderId();
    this.fullLoaderService.hideLoader();
    this.showAcceptOrder = true;
  }

  async rejectPO() {
    if(this.userkycStatus != 'APPROVED'){
      this.snack.open('Reject action is disabled until KYC is approved', 'OK', { duration: 2500 });
      return;
    }
    this.fullLoaderService.showLoader();
    await this.fetchAllRejectionReason();
    this.fullLoaderService.hideLoader();
    this.showRejectOrder = true;
  }

  async getLastAcceptedOrderId() {
    try {
      const result = await this.quoteService.getLastAcceptedServiceOrderIdByServicePoToken();
      console.log({ result });
      const lastId = (result[0]?.lastId ?? 0) + 1;

      const orderConfirmationNumber = `OC-${this.headerFormGroup.get('purchaseOrderRefNumber')?.value}-${lastId
        .toString()
        .padStart(2, '0')}`;

      console.log({
        lastId,
        orderConfirmationNumber,
      });

      this.orderConfirmationNumber = orderConfirmationNumber;
    } catch (error) {
      console.error('Error getting last accepted order id:', error);
      throw error;
    }
  }

  async fetchAllRejectionReason() {
    try {
      const result: any = await this.quoteService.fetchAllServiceRejectionReasonByServicePoToken();
      console.log({ result });

      this.rejectionReasons = result.map((r: any) => ({
        id: r.id,
        reason: r.reason
      }));

      console.log('rejectionReasons', this.rejectionReasons);
    } catch (error) {
      console.error('Error getting Rejection Reason:', error);
      throw error;
    }
  }

  async acceptOrder() {
    const poPayload = this.buildServicePurchaseOrderPayload();
    console.log({ poPayload });

    const params = {
      orderConfirmationNumber: this.orderConfirmationNumber,
      estimatedDeliveryDate: this.deliveryDate || null,
      estimatedShippingDate: this.shippingDate || null,
      purchaseOrderRefNumber: this.headerFormGroup.get('purchaseOrderRefNumber')?.value,
      buyerName: this.buyerName,
      rfqRefNumber: this.headerFormGroup.get('rfqRefNumber')?.value,
      supplierName: this.supplierName,
      buyerEmail: this.headerFormGroup.get('buyerEmail')?.value,
      supplierEmail: this.headerFormGroup.get('supplierEmail')?.value,
      buyerOrganizationLogo: this.buyerLogo,
      supplierOrganizationLogo: this.supplierLogo,
      buyerId: this.buyerId,
      supplierId: Number(this.servicePoTokenAuth.getSupplierId()),
      rfqId: Number(this.rfqId),
      quotationId: Number(this.quotationId),
      purchaseOrderId: Number(this.purchaseOrderId),
      supplierCompanyName: this.headerFormGroup.get('supplierCompanyName')?.value,
      pdfPayload: poPayload,
      isTokenFlow: true
    };

    console.log('Order Accepted:', { params });
    this.showAcceptOrder = false;
    this.fullLoaderService.showLoader();
    await this.quoteService.sendServicePOConfirmationAcknowledgementByServicePoToken(params);
    await this.downloadPO();
    this.fullLoaderService.hideLoader();
    this.showOrderConfirmed = true;
  }

  async rejectOrder() {
    console.log('Selected Reason ID:', this.rejectionReason);
    console.log('Message:', this.rejectionMessage);

    const selectedReason = this.rejectionReasons.find(
      r => r.id === Number(this.rejectionReason)
    );

    console.log('selectedReason', selectedReason);

    const poPayload = this.buildServicePurchaseOrderPayload();
    console.log({ poPayload });

    const params = {
      reasonId: this.rejectionReason,
      rejectReason: selectedReason?.reason,
      rejectionMessage: this.rejectionMessage,
      purchaseOrderRefNumber: this.headerFormGroup.get('purchaseOrderRefNumber')?.value,
      buyerName: this.buyerName,
      rfqRefNumber: this.headerFormGroup.get('rfqRefNumber')?.value,
      supplierName: this.supplierName,
      buyerEmail: this.headerFormGroup.get('buyerEmail')?.value,
      supplierEmail: this.headerFormGroup.get('supplierEmail')?.value,
      buyerOrganizationLogo: this.buyerLogo,
      supplierOrganizationLogo: this.supplierLogo,
      buyerId: this.buyerId,
      supplierId: Number(this.servicePoTokenAuth.getSupplierId()),
      rfqId: Number(this.rfqId),
      quotationId: Number(this.quotationId),
      purchaseOrderId: Number(this.purchaseOrderId),
      supplierCompanyName: this.headerFormGroup.get('supplierCompanyName')?.value,
      pdfPayload: poPayload,
      alternativeSuggested: 'No',
      isTokenFlow: true
    };

    console.log('Order Rejected:', { params });
    this.showRejectOrder = false;
    this.fullLoaderService.showLoader();
    await this.quoteService.sendServicePORejectionAcknowledgementByServicePoToken(params);
    this.fullLoaderService.hideLoader();

    this.snack.open('Service PO has been rejected', 'OK', { duration: 3000 });
    await this.loadPurchaseOrderData();
  }

  cancel() {
    this.showAcceptOrder = false;
    this.showRejectOrder = false;
    this.showOrderConfirmed = false;
  }

  closeAcceptOrderConfirmation() {
    this.showOrderConfirmed = false;
    this.snack.open('Service PO has been accepted', 'OK', { duration: 3000 });
  }

  goToConfirmedOrderOverview() {
    this.servicePoTokenAuth.clear();
    this.router.navigate(['/servicePoTokenLogin']);
  }

  async loadKycStatus(supplierId: number): Promise<void> {
   
    try {
      this.kycLoader = true
      const res: any = await this.kycService.fetchSupplierKycDataByServicePoToken(supplierId);
      
      console.log('[KYC][dashboard] raw response:', res);

      const rows: any[] = Array.isArray(res)
        ? res
        : Array.isArray(res?.rows)
        ? res.rows
        : Array.isArray(res?.data?.rows)
        ? res.data.rows
        : [];

      console.log('[KYC][dashboard] normalized row count:', rows.length);

      const latestRow = rows.length > 0 ? rows[0] : null;

      console.log(latestRow?.status,'latestRow?.status')

      const kycStatus = latestRow?.status || 'NOT_STARTED';
      this.userkycStatus = latestRow?.status || 'NOT_STARTED';
      const kycReferenceNumber = latestRow?.kycReferenceNumber || null;

      this.isKycCompleted = kycStatus === 'APPROVED';
      this.showKycAlert = kycStatus != 'APPROVED';

      console.log('[KYC][dashboard] status:', kycStatus);
      console.log('[KYC][dashboard] reference:', kycReferenceNumber);
      this.kycLoader = false

    } catch (error: any) {
      console.error('[KYC][dashboard] error:', error);
     
      
    } finally {
      // this.loading = false;
      this.kycLoader = false
    }
  }

  async loadDashboard(supplierId: number): Promise<void> {
    // this.loading = true;
    try {
      const res: any = await this.kycService.fetchSupplierKycDataByServicePoToken(supplierId);
      
      console.log('[KYC][dashboard] raw response:', res);

      const rows: any[] = Array.isArray(res)
        ? res
        : Array.isArray(res?.rows)
        ? res.rows
        : Array.isArray(res?.data?.rows)
        ? res.data.rows
        : [];

      console.log('[KYC][dashboard] normalized row count:', rows.length);

      const latestRow = rows.length > 0 ? rows[0] : null;

      console.log(latestRow?.status,'latestRow?.status')

      const kycStatus = latestRow?.status || 'NOT_STARTED';
      this.userkycStatus = latestRow?.status || 'NOT_STARTED';
      const kycReferenceNumber = latestRow?.kycReferenceNumber || null;

      this.isKycCompleted = kycStatus === 'APPROVED';

      console.log('[KYC][dashboard] status:', kycStatus);
      console.log('[KYC][dashboard] reference:', kycReferenceNumber);


    } catch (error: any) {
      console.error('[KYC][dashboard] error:', error);
      
    } finally {
      // this.loading = false;
    }
  }


  async startKyc(): Promise<void> {
    const supplierIdToUse = this.servicePoTokenAuth.getSupplierId() ? Number(this.servicePoTokenAuth.getSupplierId()) : null;
    if (!supplierIdToUse) {
      this.snack.open('Missing supplier identity', 'OK', { duration: 2500 });
      return;
    }
    this.startingKyc = true;
    try {
      // Make sure lookups are ready before opening the wizard so dropdowns
      // are populated from the very first paint.
      if (!this.lookups) {
        await this.loadLookups();
      }
      const created = await this.kycService.startKycByServicePoToken({
        supplierId: supplierIdToUse,
        companyId: this.servicePoTokenAuth.getCompanyIdId() ? Number(this.servicePoTokenAuth.getCompanyIdId()) : null,
      });
      this.selectedKycId = created.kycId;
      this.selectedReference = created.kycReferenceNumber;
      this.selectedReadOnly = this.computeReadOnly(created.status);
      this.showWizard = true;
      await this.loadDashboard(supplierIdToUse);
    } catch (error: any) {
      this.snack.open(
        error?.error?.message || error?.message || 'Failed to start KYC',
        'OK',
        { duration: 3000 }
      );
    } finally {
      this.startingKyc = false;
    }
  }

  async loadLookups(): Promise<void> {
    try {
      const raw: any = await this.kycService.getLookupsByServicePoToken();
      console.log('[KYC][lookups] raw response:', raw);
      // Tolerate { ... } or { data: { ... } } envelope
      const payload: any = raw?.businessTypes ? raw : raw?.data?.businessTypes ? raw.data : raw;
      this.lookups = {
        businessTypes: payload?.businessTypes || [],
        bankAccountTypes: payload?.bankAccountTypes || [],
        documentTypes: payload?.documentTypes || [],
        certifications: payload?.certifications || [],
      } as KycLookups;
      console.log('[KYC][lookups] parsed counts:', {
        businessTypes: this.lookups.businessTypes.length,
        bankAccountTypes: this.lookups.bankAccountTypes.length,
        documentTypes: this.lookups.documentTypes.length,
        certifications: this.lookups.certifications.length,
      });
    } catch (error: any) {
      console.error('[KYC][lookups] error:', error);
    }
  }

  
  private computeReadOnly(status: string): boolean {
    // For the supplier themselves, only IN_PROGRESS and CLARIFICATION_REQUIRED
    // are editable. All terminal / awaiting-admin states are view-only.
    return !(status === 'IN_PROGRESS' || status === 'CLARIFICATION_REQUIRED');
  }

  onWizardSaved(payload: { status?: string }): void {
    const supplierIdToUse = this.servicePoTokenAuth.getSupplierId() ? Number(this.servicePoTokenAuth.getSupplierId()) : null;
    if (supplierIdToUse) this.loadDashboard(supplierIdToUse);
    // Push the new status into the singleton so the sidebar gating, the
    // dashboard banner, and the route guard pick it up immediately without
    // having to wait for the next page navigation.
    if (payload?.status) {
      this.kycStatus.setStatus(payload.status as KycStatus);
    }
    // Re-fetch from server to capture any server-side derived fields
    // (kycReferenceNumber on first START_KYC, etc.).
    this.kycStatus.refresh(true).catch(() => {});
  }

  closeWizard(): void {
    this.showWizard = false;
    const supplierIdToUse = this.servicePoTokenAuth.getSupplierId() ? Number(this.servicePoTokenAuth.getSupplierId()) : null;
    if (supplierIdToUse) this.loadDashboard(supplierIdToUse);
  }

  proceed() {
    this.showKycAlert = false
    this.startKyc()
  }

  get copy(): KycCopy {
    switch (this.userkycStatus) {
      case 'IN_PROGRESS':
        return {
          variant: 'resume',
          iconName: 'edit_note',
          title: 'Resume your KYC verification',
          body:
            "You've already started your KYC application but haven't submitted it yet. Pick up where you left off — it only takes a few minutes.",
          ctaLabel: 'Resume KYC',
          laterLabel: 'Remind me later',
          showSteps: true,
        };

      case 'SUBMITTED':
        return {
          variant: 'review',
          iconName: 'hourglass_empty',
          title: 'Your KYC is under review',
          body:
            'Thanks for submitting your KYC! Our compliance team is reviewing your application and will get back to you within 2 to 3 business days. You\'ll be notified by email and in your portal inbox once a decision is made.',
          ctaLabel: null,
          laterLabel: 'Got it',
          showSteps: false,
        };

      case 'UNDER_REVIEW':
        return {
          variant: 'review',
          iconName: 'pending_actions',
          title: 'Verification in progress',
          body:
            'Our compliance team is actively reviewing your KYC application. You\'ll receive an email and in-app notification as soon as a decision is made — typically within 2 to 3 business days.',
          ctaLabel: null,
          laterLabel: 'Got it',
          showSteps: false,
        };

      case 'CLARIFICATION_REQUIRED':
        return {
          variant: 'clarify',
          iconName: 'help_outline',
          title: 'Action required: clarification needed',
          body:
            'Our compliance team has requested some clarifications on your KYC application. Open your application to review their notes and respond.',
          ctaLabel: 'Open my KYC',
          laterLabel: 'Later',
          showSteps: false,
        };

      case 'REJECTED':
        return {
          variant: 'rejected',
          iconName: 'report_problem',
          title: 'Your KYC needs revision',
          body:
            'Your KYC application was not approved. Please review the feedback inside the application, update your details, and resubmit.',
          ctaLabel: 'Review & resubmit',
          laterLabel: 'Later',
          showSteps: false,
        };

      case 'NOT_STARTED':
      default:
        return {
          variant: 'start',
          iconName: 'verified_user',
          title: 'Verify your business to begin',
          body:
            'Welcome to Varuna Sentinels Marketplace. Before you can transact, please complete a quick KYC verification — it takes about 5 minutes.',
          ctaLabel: 'Start KYC',
          laterLabel: 'Remind me later',
          showSteps: true,
        };
    }
  }

  async generatePoExcel() {

    if(!this.purchaseOrderId || !this.headerFormGroup.get('purchaseOrderRefNumber')?.value){
      this.snack.open('File can not be downloaded', 'OK', {
        duration: 3000,
      });
      return;
    }

    this.fullLoaderService.showLoader('Download in progress. Please hold on...');
    try {
      const fileName = this.headerFormGroup.get('purchaseOrderRefNumber')?.value + '.xlsx';
      const result = await this.quoteService.fetchServicePoAttachmentExcelDataByPoToken({
        purchaseOrderId: Number(this.purchaseOrderId),
      });

      if (!result || !result.pdfData) {
        this.snack.open('No data found for this PO.', 'OK', { duration: 3000 });
        return;
      }

      const data = {
        purchaseOrderDetails: result.pdfData.purchaseOrder,
        buyerDetails: {
          name: result.pdfData.buyer?.company?.name,
          address: result.pdfData.buyer?.company?.address,
          contact: result.pdfData.buyer?.contact?.person,
          phone: result.pdfData.buyer?.company?.phone,
          email: result.pdfData.buyer?.contact?.email,
          billingAddress: result.pdfData.termsAndConditions?.billingAddress,
        },
        supplierDetails: {
          name: result.pdfData.supplier?.companyName,
          address: result.pdfData.supplier?.address,
          contact: result.pdfData.supplier?.contactName,
          phone: result.pdfData.supplier?.phone,
          email: result.pdfData.supplier?.email,
        },
        // Freight Forwarder Details - Removed from service flow
        portAgentDetails: {
          name: result.pdfData.portAgent?.portAgencyName,
          phone: result.pdfData.portAgent?.phoneNumber,
          fax: result.pdfData.portAgent?.phoneFax,
          email: result.pdfData.portAgent?.contact,
          contact: result.pdfData.portAgent?.contact,
        },
        costSummary: {
          lineItemSubtotal: parseFloat(result.pdfData.priceDetails?.subtotal || 0).toFixed(3),
          generalDiscountType: result.pdfData.priceDetails?.generalDiscount?.type || 'percentage',
          generalDiscountValue: parseFloat(result.pdfData.priceDetails?.generalDiscount?.value || 0),
          generalDiscountAmount: (result.pdfData.priceDetails?.generalDiscount?.type || 'percentage') === 'percentage'
            ? (parseFloat(result.pdfData.priceDetails?.subtotal || 0) * parseFloat(result.pdfData.priceDetails?.generalDiscount?.value || 0) / 100).toFixed(3)
            : parseFloat(result.pdfData.priceDetails?.generalDiscount?.value || 0).toFixed(3),
          taxAmount: parseFloat(result.pdfData.priceDetails?.taxAmount || 0).toFixed(3),
          otherCharges: result.pdfData.priceDetails?.freight || [],
          totalAdditionalCost: result.pdfData.priceDetails?.freightTotal,
          currency: result.pdfData.priceDetails?.currency?.currencyCode || '',
          grandTotal: parseFloat(result.pdfData.priceDetails?.total || 0).toFixed(3),
        },
        vesselDetails: result.pdfData.vessel,
        portDetails: result.pdfData.port,
        priceDetails: result.pdfData.priceDetails,
        incotermDetails: result.pdfData.incotermDetails || {},
        taxDetails: result.pdfData.taxDetails || {},
        termsAndConditions: result.pdfData.termsAndConditions,
        lineItems: (result.pdfData.lineItems || []).map((item: any, index: number) => ({
          srNo: index + 1,
          serviceName: item.serviceName,
          serviceDescription: item.serviceDescription,
          quantity: item.quantity,
          unitCost: item.unitCost,
          discount: item.discount,
          discountedUnitCost: item.discountedUnitPrice,
          totalCost: item.totalCost,
        })),
      };

      const labelMap: Record<string, string> = {
        name: 'Name', address: 'Address', contact: 'Contact', phone: 'Phone',
        fax: 'Fax', email: 'Email', billingAddress: 'Billing Address', PIC: 'PIC',
      };

      const Excel = await import('exceljs');
      const workbook = new Excel.Workbook();
      const ws = workbook.addWorksheet('Service Purchase Order');

      // === TITLE ===
      ws.mergeCells('A1:G1');
      const title = ws.getCell('A1');
      title.value = 'Service Purchase Order';
      title.font = { size: 18, bold: true, color: { argb: '1F4E78' } };
      title.alignment = { horizontal: 'center', vertical: 'middle' };
      ws.addRow([]);

      // === PO DETAILS ===
      const poHeader = ws.addRow(['SERVICE PURCHASE ORDER DETAILS']);
      poHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9E1F2' } };
      poHeader.getCell(1).font = { bold: true };
      ws.addRow([]);

      const poInfo = [
        ['Purchase Order Number:', data.purchaseOrderDetails?.purchaseOrderRefNumber, '', 'Expected Delivery Date:', data.purchaseOrderDetails?.expectedDeliveryDate],
        ['Order Date:', data.purchaseOrderDetails?.purchaseOrderDate, '', 'Incoterms:', data.incotermDetails?.incotermCode || ''],
        ['Quotation Number:', data.purchaseOrderDetails?.quoteRefNumber, '', 'Incoterms Location:', data.incotermDetails?.locations || ''],
        ['Quotation Date:', data.purchaseOrderDetails?.quotationDate, '', 'Vessel Name:', data.vesselDetails?.name],
        ['Payment Terms:', data.termsAndConditions?.buyersPaymentTerms, '', 'Port Name:', data.portDetails?.name],
      ];
      poInfo.forEach((r) => {
        const row = ws.addRow(r);
        row.eachCell((c) => (c.alignment = { vertical: 'middle' }));
      });
      ws.addRow([]);

      // === BUYER + SUPPLIER DETAILS ===
      const bsHeader = ws.addRow(['Buyer Details', '', '', 'Supplier Details']);
      bsHeader.eachCell(c => {
        c.font = { bold: true };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF2CC' } };
      });
      ws.addRow([]);

      const buyerEntries = Object.entries(data.buyerDetails).map(([key, value]) => [
        labelMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()), value
      ]);
      const supplierEntries = Object.entries(data.supplierDetails || {}).map(([key, value]) => [
        labelMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()), value
      ]);
      const bmaxRows = Math.max(buyerEntries.length, supplierEntries.length);
      const paddedBuyer = [...buyerEntries, ...Array(bmaxRows - buyerEntries.length).fill(['', ''])];
      const paddedSupplier = [...supplierEntries, ...Array(bmaxRows - supplierEntries.length).fill(['', ''])];
      for (let i = 0; i < bmaxRows; i++) {
        ws.addRow([paddedBuyer[i][0], paddedBuyer[i][1], '', paddedSupplier[i][0], paddedSupplier[i][1]]);
      }
      ws.addRow([]);

      // === PORT AGENT DETAILS === (Freight Forwarder Details - Removed from service flow)
      const faHeader = ws.addRow(['Port Agent Details']);
      faHeader.eachCell(c => {
        c.font = { bold: true };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'E2EFDA' } };
      });
      ws.addRow([]);

      const portAgentEntries = Object.entries(data.portAgentDetails || {}).map(([key, value]) => [
        labelMap[key] || key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()), value
      ]);
      for (let i = 0; i < portAgentEntries.length; i++) {
        ws.addRow([portAgentEntries[i][0], portAgentEntries[i][1]]);
      }
      ws.addRow([]);

      // === LINE ITEMS ===
      const liHeader = ws.addRow(['Line Items']);
      liHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'BDD7EE' } };
      liHeader.getCell(1).font = { bold: true };
      ws.addRow([]);

      const tableHeader = ws.addRow([
        'Sr. No', 'Service Name', 'Service Description', 'Quantity',
        'Unit Cost', 'Discount', 'Discounted Unit Cost', 'Total Cost'
      ]);
      tableHeader.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9D9D9' } };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });

      data.lineItems.forEach((item: any) => {
        const row = ws.addRow([
          item.srNo, item.serviceName, item.serviceDescription, item.quantity,
          item.unitCost, item.discount, item.discountedUnitCost, item.totalCost
        ]);
        row.eachCell((cell) => {
          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
          cell.alignment = { wrapText: true, vertical: 'middle' };
        });
      });
      ws.addRow([]);

      // === COST SUMMARY ===
      const csHeader = ws.addRow(['Cost Summary']);
      csHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F9F9F9' } };
      csHeader.getCell(1).font = { bold: true };
      ws.addRow([]);

      const csTableHeader = ws.addRow(['Description', 'Amount']);
      csTableHeader.eachCell((cell) => {
        cell.font = { bold: true };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'D9D9D9' } };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
      });

      const addCostRow = (label: string, value: any, bold = false) => {
        const row = ws.addRow([label, value]);
        row.eachCell((cell) => {
          if (bold) cell.font = { bold: true };
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
          cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
        });
      };

      addCostRow('Line Total Amount', data.costSummary.lineItemSubtotal);
      if (parseFloat(data.costSummary.generalDiscountAmount) > 0) {
        addCostRow('General Discount Amount', data.costSummary.generalDiscountAmount);
      }

      let totalAdditionalCharges = 0;
      (data.costSummary.otherCharges || []).forEach((charge: any) => {
        addCostRow(charge.description, charge.value);
        totalAdditionalCharges += Number(charge.value || 0);
      });
      addCostRow('Total Additional Cost', totalAdditionalCharges, true);

      if (parseFloat(data.costSummary.taxAmount) > 0) {
        addCostRow('Tax Amount', data.costSummary.taxAmount);
      }
      addCostRow('Grand Total', `${data.costSummary.grandTotal} ${data.costSummary.currency}`, true);
      ws.addRow([]);

      // === TERMS AND CONDITIONS ===
      const termsHeader = ws.addRow(['Terms And Conditions']);
      termsHeader.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FCE4D6' } };
      termsHeader.getCell(1).font = { bold: true };
      ws.addRow([]);
      ws.addRow([data.termsAndConditions?.text]);
      ws.addRow([]);

      // === FROM/TO ===
      ws.addRow(['From VSMPS Buyer:']);
      ws.addRow([data.buyerDetails.name]);
      ws.addRow([data.buyerDetails.address]);
      ws.addRow([]);
      ws.addRow(['To VSMPS Supplier:']);
      ws.addRow([data.supplierDetails.name]);
      ws.addRow([data.supplierDetails.address]);
      ws.addRow([]);
      ws.addRow(['Copyright \u00A9 Varuna Sentinels BV. All rights reserved.']);

      // === AUTO WIDTH ===
      const colCount = ws.actualColumnCount;
      for (let i = 1; i <= colCount; i++) {
        let maxLen = 12;
        ws.eachRow({ includeEmpty: true }, (r) => {
          const val = r.getCell(i).value;
          const len = val ? val.toString().length : 0;
          if (len > maxLen) maxLen = len;
        });
        ws.getColumn(i).width = Math.min(maxLen + 4, 40);
      }

      workbook.xlsx.writeBuffer().then(async (buffer) => {
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const fileSaverMod = await import('file-saver');
        const saveAs = (fileSaverMod as any).saveAs || (fileSaverMod as any).default;
        saveAs(blob, fileName);
      });
    } catch (error) {
      console.error('Error generating PO Excel:', error);
      this.snack.open('Failed to generate PO Excel.', 'OK', { duration: 3000 });
    } finally {
      this.fullLoaderService.hideLoader();
    }
  }
}
